// 「복수 점검 허용」(policy.allowMultipleInspections — 구 `inspectionSelection:'multi'`, 2026-10-09 이관)이 이
// 양식에서 실제로 항목을 전 점검으로 잇는지 — 서버 조용한 게이트의 admin 미러(순수 함수).
//
// ⚠️ 「복수를 허용하나」 자체는 shared `allowsMultipleInspections`/`judgeInspectionCount`가 정본이고 서버가 생성
// 시 집행한다. 이 파일이 재는 것은 그 **다음** 질문 — 허용했을 때 항목이 정말 합본되나 — 다.
//
// 서버 조립은 문서에 묶인 점검 전부(scope.linkedInspections)를 순회하는 카탈로그 entry가 **하나**뿐이다
// (report-composer.service `gatedAssembleEntries`의 `inspection-risk-assessment-rows` — run이 scope를
// 받아 전 점검을 돈다; 그 밖의 entry·헤더 조회(점검일·점검자 포함)는 첫 점검 기준). 그 entry를 주 소스로
// 쓰지 않는 양식에 복수 허용을 켜면 모달은 뜨고 점검도 묶이지만 항목은 첫 점검만 조립된다 — 서버는 아무
// 말도 하지 않으므로 저작 화면이 같은 조건으로 판정해 안내한다(CLAUDE.md 「서버 조용한 게이트 ↔ admin
// 미러」).
//
// 「어느 entry가 전 점검을 순회하나」는 admin 손 목록이 아니라 **카탈로그 entry 선언**
// (`CatalogEntryBase.traversesLinkedInspections` — shared JSDoc 정본, 서버 catalog.spec이 「선언 집합 =
// 순회 run」을 대조)으로 읽는다 — reportScoped와 같은 「admin 하드코딩 목록 금지」 규약이다.
// ⚠️ 손 목록으로 두면 순회 entry가 늘 때 여기가 조용히 빠지는 silent fail 경로가 된다.
// 카탈로그는 EditorPage가 `/admin/report-composer/catalog`로 받는다.
import { allowsMultipleInspections, type CatalogEntry, type DataSourceDef, type TemplatePolicy } from '@safetyhub/shared';

/**
 * 판정 결과 — 저작 판정 유틸 공통 어휘(`kind` 판별자 + 사유 객체 · shared
 * `judgeRowScopeWriteBack`·`judgeFindingWriteBackField`와 같은 형상).
 */
export type InspectionSelectionMultiVerdict =
  /** 복수 허용이고 전 점검 순회 소스가 있다. */
  | { kind: 'eligible' }
  | {
      kind: 'ineligible';
      reason:
        /** 복수 허용이 아니다(`allowMultipleInspections` 미선언) — 판정 대상 아님. */
        | 'not-multi'
        /** 복수 허용인데 카탈로그가 아직 없다(로드 전/실패) — 판정 불가라 경고하지 않는다(거짓 경고 방지). */
        | 'catalog-unknown'
        /** 복수 허용인데 전 점검을 순회하는 소스가 없다 — 항목은 첫 점검만 조립된다(경고 대상). */
        | 'no-traversing-source';
    };

/**
 * 복수 허용 정책이 이 양식에서 유효한지 판정한다. 주 소스(`primary`)가 `traversesLinkedInspections`를
 * 선언한 카탈로그 entry인 assembled 선언이 하나라도 있으면 유효(kind 축: manual은 primary가 ''/부재라
 * 매칭될 일이 없지만 명시적으로 건너뛴다). 카탈로그가 비어 있으면(로드 전) `catalog-unknown` —
 * 「없다」는 판정은 카탈로그를 봤을 때만 낸다. 「허용인가」는 shared 술어로 읽는다 — 여기 식을 다시 쓰지 말 것.
 */
export function judgeInspectionSelectionMulti(
  policy: TemplatePolicy | undefined,
  dataSources: DataSourceDef[] | undefined,
  catalog: readonly CatalogEntry[] | undefined,
): InspectionSelectionMultiVerdict {
  if (!allowsMultipleInspections(policy)) return { kind: 'ineligible', reason: 'not-multi' };
  if (!catalog || catalog.length === 0) return { kind: 'ineligible', reason: 'catalog-unknown' };
  const traversing = new Set(
    catalog.filter((e) => e.traversesLinkedInspections === true).map((e) => e.key),
  );
  const hasTraversing = (dataSources ?? []).some(
    (d) => d.kind === 'assembled' && traversing.has(d.primary),
  );
  return hasTraversing
    ? { kind: 'eligible' }
    : { kind: 'ineligible', reason: 'no-traversing-source' };
}
